
import React from 'react';
import { useLanguage } from '../../contexts/language';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PeopleGroup } from '../../data/types/region-types';
import { Region } from '../../data/types/region-types';
import { ChevronDown, ChevronUp, Globe, Languages, Scroll } from 'lucide-react';
import { getPrimaryLanguagesInRegion, getPrimaryReligionsInRegion } from '../../data/utils/region-utils';

interface UnreachedGroupsProps {
  unreachedGroups: PeopleGroup[];
  showData: boolean;
  onToggle: () => void;
  unreachedPopulation?: number;
  region: Region;
}

const UnreachedGroups: React.FC<UnreachedGroupsProps> = ({ 
  unreachedGroups, 
  showData, 
  onToggle,
  unreachedPopulation,
  region
}) => {
  const { t } = useLanguage();
  const primaryLanguages = getPrimaryLanguagesInRegion(region.region_id);
  const primaryReligions = getPrimaryReligionsInRegion(region.region_id);
  
  return (
    <div className="mt-8 mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-bold text-purple-700">{t('unreachedPeoples')}</h3>
        <button 
          className="flex items-center px-3 py-1.5 text-sm rounded-full bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
          onClick={onToggle}
        >
          {showData ? (
            <>
              {t('hide')}
              <ChevronUp className="h-4 w-4 ml-1.5" />
            </>
          ) : (
            <>
              {t('show')}
              <ChevronDown className="h-4 w-4 ml-1.5" />
            </>
          )}
        </button>
      </div>
      
      {unreachedPopulation && (
        <div className="mt-3 mb-2 space-y-3">
          <p className="text-base text-gray-700">
            {t('estimatedUnreachedPopulation')}: <span className="font-bold text-purple-800">{unreachedPopulation.toLocaleString()}</span>
          </p>
          
          <div className="flex flex-wrap gap-2 items-center text-sm">
            <div className="flex items-center gap-1 text-purple-800 bg-purple-50 px-2 py-1 rounded-md">
              <Globe className="h-3.5 w-3.5" />
              <span>{unreachedGroups.length} {t('peopleGroups')}</span>
            </div>
            
            <div className="flex items-center gap-1 text-blue-800 bg-blue-50 px-2 py-1 rounded-md">
              <Languages className="h-3.5 w-3.5" />
              <span>{primaryLanguages.length} {t('languages')}</span>
            </div>
            
            <div className="flex items-center gap-1 text-amber-800 bg-amber-50 px-2 py-1 rounded-md">
              <Scroll className="h-3.5 w-3.5" />
              <span>{primaryReligions.length} {t('religions')}</span>
            </div>
          </div>
        </div>
      )}
      
      {showData && unreachedGroups.length > 0 && (
        <div className="mt-4 space-y-4">
          <p className="text-sm text-gray-600 italic">
            {t('joshuaProjectData')}
          </p>
          
          {unreachedGroups.map(group => (
            <Card key={group.id} className="border border-gray-200 overflow-hidden">
              <CardHeader className="py-3 px-4 bg-purple-50">
                <CardTitle className="text-base font-bold text-purple-800">{group.name}</CardTitle>
              </CardHeader>
              <CardContent className="py-3 px-4 text-sm space-y-2">
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-700 font-medium">{t('population')}:</span>
                  <span className="text-gray-900 font-semibold">{group.population.toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-700 font-medium">{t('evangelicalPercentage')}:</span>
                  <span className="text-gray-900 font-semibold">{group.evangelical_percentage}%</span>
                </div>
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-700 font-medium">{t('primaryReligion')}:</span>
                  <span className="text-gray-900 font-semibold">{group.primary_religion}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-700 font-medium">{t('primaryLanguage')}:</span>
                  <span className="text-gray-900 font-semibold">{group.primary_language}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default UnreachedGroups;
